// Метки на карточке кандидата.
//
// Метка — это отбор, а не украшение: по ней рекрутёр собирает свою подборку («звонить в
// понедельник», «Пермь», «есть 6 разряд») и возвращается к ней ссылкой. Поэтому уже
// проставленные метки показаны кнопками — нажатие открывает список, отобранный по ней.
//
// Метки ДОБАВЛЯЮТСЯ. Снятия у сервера нет вовсе (`POST /candidates/{id}/tags` только
// дописывает набор), и врать про это кнопкой «убрать» нельзя: она бы либо ничего не делала,
// либо молча теряла остальные метки. Ограничение записано в отчёте.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { Link } from 'react-router';

import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { useAddTagsMutation, useCandidateQuery } from '../queries';
import { FILTER_TAG, hiringHref } from '../route';

const EDIT_PERMISSION = 'hiring.candidate.edit';

export function TagsPanel({ candidateId, onClose }: { candidateId: string; onClose: () => void }) {
  const allowed = useCan(EDIT_PERMISSION);
  const кандидат = useCandidateQuery(candidateId);
  const мутация = useAddTagsMutation(candidateId);

  const [ввод, setВвод] = useState('');
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [отказ, setОтказ] = useState<string | null>(null);

  const метки = кандидат.data?.tags ?? [];

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(
        'Ставить метки может рекрутёр и кадры. Попросите администратора выдать право на правку карточек.',
      );
      return;
    }
    if (мутация.isPending) {
      setОтказ('Метки уже сохраняются, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    const новые = разобрать(ввод);
    if (новые.length === 0) {
      setОшибка('Напишите хотя бы одну метку. Несколько — через запятую.');
      return;
    }
    setОшибка(undefined);
    setОтказ(null);
    мутация.mutate(новые, {
      onSuccess: () => {
        setВвод('');
        onClose();
      },
    });
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!allowed && <NoPermissionNote что='ставить метки' кто='Карточки ведут рекрутёр и кадры.' />}

      <div className='flex flex-col gap-1'>
        <span className='text-paragraph-xs text-text-sub-600'>Уже стоят</span>
        {кандидат.data === undefined && кандидат.isPending ? (
          <Skeleton className='h-5 w-40' />
        ) : метки.length === 0 ? (
          <span className='text-paragraph-sm text-text-sub-600'>меток нет</span>
        ) : (
          <div className='flex flex-wrap gap-1.5'>
            {метки.map((метка) => (
              <Link
                key={метка}
                to={hiringHref('candidates', { filters: { [FILTER_TAG]: метка }, panels: [] })}
                aria-label={`Показать всех с меткой «${метка}»`}
              >
                <Badge.Root variant='lighter' color='blue' size='medium'>
                  {метка}
                </Badge.Root>
              </Link>
            ))}
          </div>
        )}
      </div>

      <TextField
        id='candidate-tags'
        label='Добавить метки'
        required
        value={ввод}
        onChange={setВвод}
        placeholder='Пермь, звонить в понедельник'
        error={ошибка}
        hint='Несколько меток — через запятую. Нажатие на готовую метку открывает список всех, у кого она стоит.'
      />

      <Hint.Root>
        Метки только добавляются: снять уже поставленную сервер пока не умеет. Если метка ошибочная
        — заведите рядом верную, отбор работает по любой из них.
      </Hint.Root>

      {мутация.isError && <ErrorAlert error={мутация.error} />}
      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {мутация.isPending ? 'Сохраняем…' : 'Добавить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

/** «Пермь, звонить в понедельник» → две метки. Пустые куски и повторы отбрасываются. */
function разобрать(ввод: string): string[] {
  const собрано: string[] = [];
  for (const кусок of ввод.split(',')) {
    const метка = кусок.trim();
    if (метка !== '' && !собрано.includes(метка)) {
      собрано.push(метка);
    }
  }
  return собрано;
}
